import React, { useEffect, useState, useCallback } from 'react';
import { FormGroup, Label, FormText, Row, Col, Card } from 'reactstrap';
import Select from 'react-select';
import useVendorBranch from '../utils/useVendorBranch';
import debounce from 'lodash.debounce';

export default function VendorBranchSelector({
  user,
  selectionData,
  setSelectionData,
  errorMessage,
  accessButton = true,
}) {
  const {
    venderMaster,
    branchMaster,
    fetchVendors,
    fetchBranches,
    isSuperAdmin,
    isAdmin,
    accessibleBranchIds,
    hasMultiBranch,
    hasSingleBranch,
  } = useVendorBranch(user);

  const [vendorSearch, setVendorSearch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');

  // Debounce the search so API isn't called on every keystroke
  const debouncedFetchVendors = useCallback(
    debounce((search) => {
      if (isSuperAdmin) fetchVendors(1, 20, search);
    }, 500),
    [],
  );

  const debouncedFetchBranches = useCallback(
    debounce((search) => {
      const vendorId = isSuperAdmin ? selectionData.vendor_id : null;
      fetchBranches(vendorId, 1, 20, search);
    }, 500),
    [selectionData.vendor_id],
  );

  useEffect(() => {
    const loadData = async () => {
      if (isSuperAdmin) {
        await fetchVendors(1, 20, ''); // fetch default vendors
        // If vendor already selected (edit mode), fetch its branches
        if (selectionData.vendor_id) {
          const branches = await fetchBranches(
            selectionData.vendor_id,
            1,
            10,
            '',
          );
          if (branches && branches.length === 1) {
            setSelectionData((prev) => ({
              ...prev,
              branch_id: branches[0].id,
            }));
          }
        }
      }

      if (isAdmin || hasMultiBranch) {
        const branches = await fetchBranches(null, 1, 20, '');
        if (branches && branches.length === 1) {
          setSelectionData((prev) => ({ ...prev, branch_id: branches[0].id }));
        }
      }
    };

    loadData();
  }, [selectionData.vendor_id]);

  //auto select first vendor
  useEffect(() => {
    if (isSuperAdmin && venderMaster.length > 0 && !selectionData.vendor_id) {
      const firstVendor = venderMaster[0];
      setSelectionData((prev) => ({ ...prev, vendor_id: firstVendor.id }));
    }
  }, [venderMaster]);

  //auto select first branch
  useEffect(() => {
    if (branchMaster.length > 0 && !selectionData.branch_id) {
      const firstBranch = branchMaster[0];
      if (firstBranch?.id) {
      setSelectionData((prev) => ({
        ...prev,
        branch_id: firstBranch.id
      }));
    }
    }
  }, [branchMaster]);

  const handleVendorChange = async (selected) => {
    const value = selected ? selected.value : '';
    setSelectionData((prev) => ({ ...prev, vendor_id: value, branch_id: '' }));
    //if (isSuperAdmin && value) {
    const branches = await fetchBranches(value, 1, 20, '');
    // If only one branch is returned, auto-select it
    if (branches && branches.length === 1) {
      setSelectionData((prev) => ({ ...prev, branch_id: branches[0].id }));
      //}
    }
  };

  const handleBranchChange = (selected) => {
    const value = selected ? selected.value : '';
    setSelectionData((prev) => ({ ...prev, branch_id: value }));
  };
  // Show NOTHING unless user is SuperAdmin or Admin
  if (!isSuperAdmin && !isAdmin && !hasMultiBranch) {
    return null; // Normal user with only 1 branch: hide UI
  }

  // Show NOTHING if:
  // 1️⃣ User is not SuperAdmin/Admin/hasMultiBranch
  // 2️⃣ AND only 1 vendor & 1 branch available
  const showCard =
    (isSuperAdmin && venderMaster.length > 1) || // superadmin with multiple vendors
    (isAdmin && branchMaster.length > 1) || // admin with multiple branches
    hasMultiBranch; // normal multi-branch user

  if (!showCard) return null;


  return (
    <Card className='card-box pd-20 mb-20'>
      <Row>
        {/* Vendor dropdown - visible only for SuperAdmin */}
        {isSuperAdmin && (
          <Col xl={6}>
            <FormGroup>
              <Label for='vendor_id'>Vendor Name</Label>
              <Select
                isDisabled={!accessButton}
                options={venderMaster.map((v) => ({
                  value: v.id,
                  label: v.vender_name,
                }))}
                value={
                  venderMaster.find((v) => v.id === selectionData.vendor_id)
                    ? {
                        value: selectionData.vendor_id,
                        label: venderMaster.find(
                          (v) => v.id === selectionData.vendor_id,
                        )?.vender_name,
                      }
                    : null
                }
                onChange={handleVendorChange}
                onInputChange={(input) => {
                  setVendorSearch(input);
                  debouncedFetchVendors(input);
                }}
                placeholder='Search Vendor...'
                isClearable
              />
              {errorMessage.vendor_id && (
                <FormText color='danger'>{errorMessage.vendor_id}</FormText>
              )}
            </FormGroup>
          </Col>
        )}

        {/* Branch dropdown */}
        {((isSuperAdmin && selectionData.vendor_id) || // superadmin: only after vendor selected
          isAdmin || // admin: always show
          hasMultiBranch) && ( // normal multi-branch: show
          <Col xl={6}>
            <FormGroup>
              <Label for='branch_id'>Branch Name</Label>
              <Select
                isDisabled={!accessButton}
                options={branchMaster.map((b) => ({
                  value: b.id,
                  label: b.branch_name,
                }))}
                value={
                  branchMaster.find((b) => b.id === selectionData.branch_id)
                    ? {
                        value: selectionData.branch_id,
                        label: branchMaster.find(
                          (b) => b.id === selectionData.branch_id,
                        )?.branch_name,
                      }
                    : null
                }
                onChange={handleBranchChange}
                onInputChange={(input) => {
                  setBranchSearch(input);
                  debouncedFetchBranches(input);
                }}
                placeholder='Search Branch...'
                isClearable={hasMultiBranch} // only allow clear if multi branch
              />
              {errorMessage.branch_id && (
                <FormText color='danger'>{errorMessage.branch_id}</FormText>
              )}
            </FormGroup>
          </Col>
        )}
      </Row>
    </Card>
  );
}
